import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, MessageCircleQuestion, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DataSaverToggle } from "@/components/DataSaverToggle";
import { Input } from "@/components/ui/input";
import { useLang } from "@/context/LangContext";
import { SEARCH_UI as UI } from "@/data/quickSearchI18n";
import { useSearchIndex } from "@/hooks/useSearchIndex";
import { searchItems } from "@/lib/search";
import { fill } from "@/lib/utils";

const GROUPS = ["pages", "manasik", "duas", "faq"];

// Bottom-sheet quick search (one-hand mode): pages, manasik stages, duas and FAQ; falls back to Tanya Manasik.
export const QuickSearch = ({ open, onOpenChange }) => {
  const { tc } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const index = useSearchIndex(open);
  const ready = q.trim().length >= 2;
  const results = ready ? { pages: searchItems(index.pages, q), manasik: searchItems(index.stages, q), duas: searchItems(index.duas, q), faq: searchItems(index.faq, q, 3) } : null;
  const total = results ? GROUPS.reduce((n, g) => n + results[g].length, 0) : 0;

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 150); else setQ(""); }, [open]);

  const go = (to) => { onOpenChange(false); navigate(to); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85dvh] rounded-t-3xl px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-5 flex flex-col gap-4" data-testid="quick-search-sheet">
        <SheetHeader className="text-start space-y-1">
          <div className="flex items-center justify-between gap-3 pe-8">
            <SheetTitle className="font-serif text-xl">{tc(UI.title)}</SheetTitle>
            <DataSaverToggle className="sm:hidden" />
          </div>
        </SheetHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={tc(UI.placeholder)} enterKeyHint="search"
            data-testid="quick-search-input" className="h-12 rounded-2xl ps-11 text-base" />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto space-y-5" data-testid="quick-search-results">
          {!ready && <p className="text-sm text-muted-foreground">{tc(UI.hint)}</p>}
          {ready && index.loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
          {results && GROUPS.filter((g) => results[g].length).map((g) => (
            <section key={g} data-testid={`quick-search-group-${g}`}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{tc(UI[g])}</p>
              <ul className="divide-y divide-border/50 rounded-2xl border border-border/50 bg-card">
                {results[g].map((r, i) => (
                  <li key={`${r.to}-${i}`}>
                    <button type="button" onClick={() => go(r.to)} data-testid={`quick-search-result-${g}-${i}`}
                      className="flex w-full min-h-[48px] items-center gap-3 px-4 py-3 text-start active:bg-muted/60">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{r.label}</span>
                        {r.sub && <span className="block truncate text-xs text-muted-foreground">{r.sub}</span>}
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:-scale-x-100" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {results && !index.loading && total === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="quick-search-empty">{fill(tc(UI.noResults), { q: q.trim() })}</p>
          )}
          {q.trim().length >= 3 && !index.loading && (
            <button type="button" onClick={() => go(`/?ask=${encodeURIComponent(q.trim())}`)} data-testid="quick-search-ask"
              className="flex w-full min-h-[52px] items-center gap-3 rounded-2xl bg-primary px-4 py-3 text-start text-white active:opacity-90">
              <MessageCircleQuestion className="h-5 w-5 shrink-0" />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{fill(tc(UI.askInstead), { q: q.trim() })}</span>
              <ChevronRight className="h-4 w-4 shrink-0 rtl:-scale-x-100" />
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
